import React from 'react'
import { shape, func, bool } from 'prop-types'
import { useSelector } from 'react-redux'
import {
  selectConfigurationData,
  selectLabels
} from '../../../redux/configuration/selectors'
import { SHOW_CUTOFF_MESSAGE } from '../../../configuration/constants'
import SvgIcon from '../../SvgIcon/SvgIcon'

// STYLES
import './ShipNameCrossingCard.scss'
import ResourceAvailability from '../ResourceAvailability/ResourceAvailability'
import themeVariables from '../../../styles/scss/configuration/_variables.scss'
import { getDurationTime } from '../../../configuration/utilities'

const ShipNameCrossingCard = ({
  accommodationsAvailable,
  crossing,
  isCurrentlySelectedRoute,
  isDisabled,
  isOverNight,
  selectCrossing,
  vehiclesAvailable,
  shouldDisplayNoPassengerMessage,
  isCutOff,
  waitListAvailable
}) => {
  const labels = useSelector(selectLabels)
  const configuration = useSelector(selectConfigurationData)
  const isJune28 = dateString => {
    const datePart = dateString.split('T')[0]
    const [year, month, day] = datePart.split('-')
    return year === '2025' && month === '06' && day === '28'
  }
  const isDepartureJune28 = isJune28(crossing.departureDate)

  // 5:00am - 6:59pm NT we can consider day， 7:00pm - 4:59am NT we can consider night
  const isDayTime = hhmm => {
    const [h, m] = hhmm.split(':').map(Number)
    const mins = h * 60 + m
    return mins >= 300 && mins <= 1139
  }
  const departIsDay = isDayTime(crossing.departureTime)
  const arriveIsDay = isDayTime(crossing.arrivalTime)

  // get timezone
  const getTimeZone = routeCode => {
    const route = configuration?.ferry?.options?.routes?.find(
      rCode => rCode.code === routeCode
    )
    if (!route?.timeZone) return ''

    return route.timeZone.includes('Newfoundland')
      ? 'NT'
      : route.timeZone.includes('Atlantic')
      ? 'AT'
      : ''
  }
  const departTz = getTimeZone(crossing.routeCode)
  const arriveTz = departTz === 'AT' ? 'NT' : departTz === 'NT' ? 'AT' : ''

  const [origin, destination] = labels[crossing?.routeCode].split(' | ')

  const durationTime = getDurationTime(crossing, arriveTz, departTz)

  return (
    <div
      role={'button'}
      tabIndex={isDisabled ? -1 : 0}
      aria-hidden={isDisabled ? true : undefined}
      data-test="btn-sailing"
      className={`btn ship-name-crossing-card ${
        isCurrentlySelectedRoute ? 'current-active-route' : ''
      } ${isOverNight ? 'overnight-card' : ''} ${isDisabled ? 'disabled' : ''}`}
      key={`${crossing.departureDate}-${crossing.departureTime}`}
      onClick={() => {
        if (!isDisabled) {
          selectCrossing(crossing)
        }
      }}
      onKeyDown={e => {
        if (e.keyCode === 13 || e.keyCode === 32) {
          if (!isDisabled) {
            selectCrossing(crossing)
          }
        }
      }}
    >
      {isDepartureJune28 &&
        crossing.routeCode === 'NSPB' &&
        crossing.departureTime === '12:15' && (
          <div className="torch-container">
            <SvgIcon type="torch" />
            <div className="tooltip">
              {labels.canadaGamesTorch
                ? labels.canadaGamesTorch
                : sessionStorage.getItem('language') === 'en'
                ? 'Marine Atlantic proudly welcomes the Roly McLelahan torch onboard this sailing as part of the Orangestore 2025 Canada Games Torch Relay.'
                : `Marine Atlantique est fière d’accueillir le flambeau Roly McLenahan à bord de cette traversée dans le cadre du relais de la flamme des Jeux du Canada de 2025 d’Orangestore.`}
            </div>
          </div>
        )}
      <div
        className={`times ${
          shouldDisplayNoPassengerMessage ? 'has-no-passengers' : ''
        }`}
      >
        <div className="times-row">
          <div className="time-col depart">
            <SvgIcon type={departIsDay ? 'sun' : 'moon'} />
            <span className="time">{crossing.departureTime}</span>
            <span className="departAndArrive">{departTz}</span>
          </div>

          <div className="ship-col">
            <SvgIcon type="arrow" />
            <SvgIcon
              type="ship"
              fill={
                isDisabled
                  ? themeVariables.themeDisabledIconColor
                  : themeVariables.themeIconColor
              }
            />
            <SvgIcon type="arrow" />
          </div>

          <div className="time-col arrive">
            <SvgIcon type={arriveIsDay ? 'sun' : 'moon'} />
            <span className="time">{crossing.arrivalTime}</span>
            <span className="departAndArrive">{arriveTz}</span>
          </div>
        </div>
        <div className="port-row">
          <div className="port depart">
            <p className="city">{origin}</p>
            <p className="label">{labels?.departs}</p>
          </div>

          <div className="duration-col">
            <span className="duration">{durationTime}</span>
          </div>

          <div className="port arrive">
            <p className="city">{destination}</p>
            <p className="label">{labels?.arrives}</p>
          </div>
        </div>
      </div>
      <SvgIcon type="border" />
      {isCutOff && SHOW_CUTOFF_MESSAGE && (
        <p className="cutoff-time">{labels.departureBookingCutoffReached}</p>
      )}
      {shouldDisplayNoPassengerMessage && (
        <p className="no-passenger-message">{labels.noPassengerSpace}</p>
      )}
      <ResourceAvailability
        vehiclesAvailable={vehiclesAvailable}
        accommodationsAvailable={accommodationsAvailable}
        isFlex={true}
        waitListAvailable={waitListAvailable}
        isDisabled={isDisabled}
        shouldDisplayNoPassengerMessage={shouldDisplayNoPassengerMessage}
      />
    </div>
  )
}

ShipNameCrossingCard.propTypes = {
  accommodationsAvailable: bool,
  waitListAvailable: bool,
  crossing: shape({}).isRequired,
  isCurrentlySelectedRoute: bool,
  isDisabled: bool,
  isOverNight: bool,
  selectCrossing: func.isRequired,
  vehiclesAvailable: bool,
  isCutOff: bool
}

export default ShipNameCrossingCard
